// ============================================================================
// FITCLUTCH — CÁLCULO REAL DO FITNESS SCORE E DO IMC
// ============================================================================
// Diferencial competitivo direto: apps concorrentes mostram um "Fitness Score"
// que usuárias descrevem como decorativo / sempre parecido, porque na prática é
// só um número fixo com leve variação. Aqui os 4 sub-scores (e o score
// final) são derivados de verdade das respostas do quiz — troque as
// respostas e o resultado muda de forma coerente e auditável. Nenhuma
// chamada de IA generativa é usada nem necessária.
//
// Todas as funções são puras (sem efeitos colaterais) para poder ter
// testes unitários simples no futuro.
// ============================================================================

const clamp = (n, min = 0, max = 100) => Math.min(max, Math.max(min, n));

const EXPERIENCE_BASE = {
  iniciante: 25,
  intermediario: 55,
  avancado: 80,
  especialista: 95,
};

const DURATION_BONUS = {
  rapido: 10,
  curto: 18,
  medio: 25,
  longo: 30,
};

const DAYS_BONUS = { 2: 5, 4: 12, 6: 18, 7: 20 };

const FREQUENCY_BASE = {
  regular: 80,
  inconsistente: 50,
  nunca: 20,
};

/**
 * @param {object} answers - respostas do quiz, chaveadas pelo id de cada etapa
 *   (ex: { experience: 'intermediario', frequency: 'regular', days_per_week: 4,
 *          injuries: ['ombros'], age: 28, plan_type: 'inteligente',
 *          workout_duration: 'medio', height: 165, weight_current: 62 })
 */
export function computeFitnessScore(answers) {
  const experienceBase = EXPERIENCE_BASE[answers.experience] ?? 40;
  const durationBonus = DURATION_BONUS[answers.workout_duration] ?? 15;
  const daysBonus = DAYS_BONUS[answers.days_per_week] ?? 8;

  const forca = clamp(experienceBase + durationBonus * 0.4 + daysBonus * 0.4);

  const frequencyBase = FREQUENCY_BASE[answers.frequency] ?? 40;
  const consistencia = clamp(frequencyBase + (answers.days_per_week >= 4 ? 5 : 0));

  const injuriesCount = Array.isArray(answers.injuries)
    ? answers.injuries.filter((i) => i !== 'nenhuma').length
    : 0;
  const age = Number(answers.age) || 30;
  const ageFactor = age > 50 ? -10 : age < 20 ? -5 : 0;
  const recuperacao = clamp(
    90 - injuriesCount * 15 - (answers.days_per_week >= 6 ? 10 : 0) + ageFactor
  );

  let aderencia = 60;
  if (answers.plan_type === 'inteligente') aderencia += 15;
  const experienceRank = ['iniciante', 'intermediario', 'avancado', 'especialista'].indexOf(
    answers.experience
  );
  const durationRank = ['rapido', 'curto', 'medio', 'longo'].indexOf(answers.workout_duration);
  if (experienceRank >= 0 && durationRank >= 0 && Math.abs(experienceRank - durationRank) <= 1) {
    aderencia += 10; // duração escolhida é realista pro nível de experiência
  }
  aderencia = clamp(aderencia);

  const fitnessScore = Math.round(
    forca * 0.3 + consistencia * 0.3 + recuperacao * 0.2 + aderencia * 0.2
  );

  const metricsSorted = [
    { key: 'forca', value: forca },
    { key: 'consistencia', value: consistencia },
    { key: 'recuperacao', value: recuperacao },
    { key: 'aderencia', value: aderencia },
  ].sort((a, b) => a.value - b.value);

  return {
    forca: Math.round(forca),
    consistencia: Math.round(consistencia),
    recuperacao: Math.round(recuperacao),
    aderencia: Math.round(aderencia),
    fitnessScore,
    // "top X%" = quanto melhor o score, menor o percentil (fica entre 5 e 90,
    // nunca "top 1%" artificial nem "top 99%" desanimador)
    percentile: clamp(100 - fitnessScore, 5, 90),
    projectionMin: Math.min(99, fitnessScore + 15),
    projectionMax: Math.min(99, fitnessScore + 24),
    // sub-score mais fraco = maior oportunidade de evolução, mostrado no insight
    weakestMetricKey: metricsSorted[0].key,
  };
}

const METRIC_LABELS = {
  forca: 'Força',
  consistencia: 'Consistência',
  recuperacao: 'Recuperação',
  aderencia: 'Aderência ao objetivo',
};
export function metricLabel(key) {
  return METRIC_LABELS[key] ?? key;
}

/**
 * IMC real: peso (kg) / altura (m)²
 * @param {number} weightKg
 * @param {number} heightCm
 */
export function computeIMC(weightKg, heightCm) {
  if (!weightKg || !heightCm) return null;
  const heightM = heightCm / 100;
  const imc = weightKg / (heightM * heightM);
  return Math.round(imc * 10) / 10;
}

export function classifyIMC(imc) {
  if (imc == null) return null;
  if (imc < 18.5) return { label: 'Abaixo do peso', index: 0 };
  if (imc < 25) return { label: 'Peso saudável', index: 1 };
  if (imc < 30) return { label: 'Sobrepeso', index: 2 };
  return { label: 'Obesidade', index: 3 };
}

// ============================================================================
// Tela "profile_summary" (resumo do perfil, logo após o peso) — as 3
// classificações (tipo de corpo / estilo de vida / condicionamento /
// metabolismo) são sempre DERIVADAS de respostas já dadas antes dessa tela
// no funil (body_shape, daily_activity, push_ups/squats/flexibility,
// weight_trend) — nunca inventa pergunta nova nem depende de resposta
// futura (ex: `experience`, que só é perguntado DEPOIS dessa tela).
// ============================================================================
const BODY_TYPE_FROM_SHAPE = {
  magro: 'ectomorfo',
  cheio: 'endomorfo',
  atletico: 'mesomorfo',
  definido: 'mesomorfo',
  medio: 'mesomorfo',
};

const LIFESTYLE_FROM_ACTIVITY = {
  sentado: 'sedentario',
  ativo: 'ativo',
  misto: 'equilibrado',
  nao_sei: 'equilibrado',
};

const METABOLISM_FROM_TREND = {
  subindo: 'lento',
  caindo: 'acelerado',
  estavel: 'equilibrado',
  varia: 'variavel',
  nao_sei: 'equilibrado',
};

const SELF_ASSESSMENT_SCORES = {
  pushUps: { nenhuma: 0, '1_5': 1, '6_15': 2, '16_30': 3, '30_mais': 4 },
  squats: { nenhuma: 0, '1_10': 1, '11_25': 2, '26_50': 3, '50_mais': 4 },
  flexibility: { nao_alcanco: 0, quase: 1, facil: 2, muito_flexivel: 4, nao_sei: 1.5 },
};

function conditioningFromSelfAssessment(answers) {
  const scores = [
    SELF_ASSESSMENT_SCORES.pushUps[answers.push_ups],
    SELF_ASSESSMENT_SCORES.squats[answers.squats],
    SELF_ASSESSMENT_SCORES.flexibility[answers.flexibility],
  ].filter((n) => n != null);
  if (!scores.length) return 'iniciante';
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  if (avg < 1) return 'iniciante';
  if (avg < 2.5) return 'intermediario';
  if (avg < 3.5) return 'avancado';
  return 'especialista';
}

// ============================================================================
// Tela "consistency_chart" (gráfico com/sem FitClutch, logo após barriers)
// — a Gladis foi clara: "nada aqui é estático e sem lógica". As duas curvas
// são derivadas de verdade das respostas já dadas até esse ponto do funil
// (ainda não temos experience/frequency/plan_type — só são perguntados
// DEPOIS dessa tela — então usamos o que já foi coletado: hábitos atuais
// pro ponto de partida, e o compromisso de treino (dias/semana + duração)
// pra inclinação da curva "com FitClutch"). Troque as respostas e as duas
// curvas mudam de forma coerente, igual ao Fitness Score real.
const DAYS_COMMIT_BONUS = { 2: 10, 4: 20, 6: 30, 7: 25 };
const DURATION_COMMIT_BONUS = { rapido: 8, curto: 14, medio: 20, longo: 26 };
const ACTIVITY_START_ADJ = { sentado: -5, ativo: 10, misto: 5, nao_sei: 0 };
const SLEEP_START_ADJ = { menos_5: -8, '5_6': 0, '7_8': 5, mais_8: 5 };
const TREND_WITHOUT_ADJ = { subindo: -8, caindo: 4, estavel: 1, varia: -3, nao_sei: 0 };

export function computeConsistencyProjection(answers) {
  const condScores = [
    SELF_ASSESSMENT_SCORES.pushUps[answers.push_ups],
    SELF_ASSESSMENT_SCORES.squats[answers.squats],
    SELF_ASSESSMENT_SCORES.flexibility[answers.flexibility],
  ].filter((n) => n != null);
  const condAvg = condScores.length ? condScores.reduce((a, b) => a + b, 0) / condScores.length : 1.5;
  const startBase = (condAvg / 4) * 60 + 10;

  const activityAdj = ACTIVITY_START_ADJ[answers.daily_activity] ?? 0;
  const sleepAdj = SLEEP_START_ADJ[answers.sleep_hours] ?? 0;
  const waterCount = Number(answers.water_intake) || 4;
  const waterAdj = (waterCount - 4) * 1.5;

  const startPct = clamp(startBase + activityAdj + sleepAdj + waterAdj, 5, 85);

  const daysBonus = DAYS_COMMIT_BONUS[answers.days_per_week] ?? 12;
  const durationBonus = DURATION_COMMIT_BONUS[answers.workout_duration] ?? 14;
  const withPct = clamp(startPct + daysBonus + durationBonus, startPct + 10, 97);

  const trendAdj = TREND_WITHOUT_ADJ[answers.weight_trend] ?? 0;
  const withoutPct = clamp(startPct + trendAdj, Math.max(3, startPct - 15), withPct - 15);

  return { startPct, withPct, withoutPct };
}

/**
 * @returns {{ imc: number|null, imcPct: number, bodyType: string,
 *   lifestyle: string, conditioning: string, metabolism: string }}
 */
export function computeProfileSummary(answers) {
  const imc = computeIMC(answers.weight_current, answers.height);
  const imcPct = imc == null ? 50 : clamp(((imc - 15) / (35 - 15)) * 100, 0, 100);

  return {
    imc,
    imcPct,
    bodyType: BODY_TYPE_FROM_SHAPE[answers.body_shape] ?? 'mesomorfo',
    lifestyle: LIFESTYLE_FROM_ACTIVITY[answers.daily_activity] ?? 'equilibrado',
    conditioning: conditioningFromSelfAssessment(answers),
    metabolism: METABOLISM_FROM_TREND[answers.weight_trend] ?? 'equilibrado',
  };
}